import {
  KeyRoundIcon,
  LucideIcon,
  MonitorIcon,
  PuzzleIcon,
  SmartphoneIcon,
  TabletIcon,
} from "lucide-react";
import type {
  ActiveSession,
  SecurityEvent,
} from "@/hooks/queries/securityQueries";

const PROVIDER_NAMES: Record<string, string> = {
  google: "Google",
  apple: "Apple",
  github: "GitHub",
  gitlab: "GitLab",
  password: "password",
  password_reset: "a password reset link",
  oauth: "app authorization",
};

export const providerName = (method: string) =>
  PROVIDER_NAMES[method] ?? method;

export const sessionIcon = (session: ActiveSession): LucideIcon => {
  if (session.client === "oauth") return KeyRoundIcon;
  if (session.client.startsWith("extension")) return PuzzleIcon;
  if (session.deviceType === "mobile") return SmartphoneIcon;
  if (session.deviceType === "tablet") return TabletIcon;
  return MonitorIcon;
};

export const sessionTitle = (session: ActiveSession) => {
  if (session.client === "oauth") {
    return `App access${session.oauthClientId ? ` (${session.oauthClientId})` : ""}`;
  }
  if (session.client === "extension-chrome")
    return "UniDeck extension for Chrome";
  if (session.client === "extension-firefox")
    return "UniDeck extension for Firefox";

  const browser = session.browser ?? "Unknown browser";
  return session.os ? `${browser} on ${session.os}` : browser;
};

const EVENT_TEXT: Record<string, string> = {
  login_succeeded: "Signed in",
  login_failed: "Failed sign-in attempt",
  logout: "Signed out",
  session_revoked: "Signed out a session",
  sessions_revoked: "Signed out all other sessions",
  refresh_reuse_detected: "Stopped a copied session token",
  password_changed: "Password changed",
  password_set: "Password added",
  password_reset: "Password reset by email",
  password_reset_requested: "Password reset email requested",
  email_verified: "Email address verified",
  social_connected: "Connected an account",
  account_claimed: "Old password turned off after a verified sign-in",
  account_delete_requested: "Account deletion requested",
  oauth_client_authorized: "Authorized an app",
};

export const eventText = (event: SecurityEvent) => {
  const base = EVENT_TEXT[event.type] ?? event.type.replace(/_/g, " ");
  const provider = (event.metadata?.provider ?? event.metadata?.method) as
    string | undefined;
  if (event.type === "social_connected" && provider) {
    return `Connected ${providerName(provider)}`;
  }
  if (event.type === "login_succeeded" && provider && provider !== "password") {
    return `Signed in with ${providerName(provider)}`;
  }
  if (event.type === "oauth_client_authorized" && event.metadata?.clientName) {
    return `Authorized ${event.metadata.clientName}`;
  }
  return base;
};

export const isWarningEvent = (event: SecurityEvent) =>
  event.type === "login_failed" ||
  event.type === "refresh_reuse_detected" ||
  event.type === "account_claimed";

export const eventDevice = (event: SecurityEvent) => {
  const parts = [
    event.client?.startsWith("extension") ? "Extension" : null,
    event.browser,
    event.os,
  ].filter(Boolean);
  return parts.join(" · ");
};
